import { EditOutlined,StopOutlined,UndoOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../../../../../../../shared/frontend/ui";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import type { ModalFuncProps } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import {
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_CANCELLED,
  CONTRACT_STATUS_ENDED,
  CONTRACT_STATUS_PAUSED
} from "../../../app/constants/contractStatus";
import { buildPersonContractRows } from "./personContractRows";
import type {
  ApiContractPackageDto,
  ApiPersonServiceDto,
  ContractCreateFormValues,
  ContractRow,
  ContractTemplateBlockDto,
  PersonServiceRow
} from "./personDetailTypes";
import {
  compareDateOnlyDesc,
  compareTextDesc,
  isEndedByDate,
  mapApiPackageToContractRow
} from "./personDetailUtils";
import { PERSON_DETAIL_LOOKUP_STALE_TIME_MS,personDetailQueryKeys } from "./personDetailQueryKeys";

type PersonContractListControllerArgs = {
  api: ApiClient;
  personId: string;
  canCancelContracts: boolean;
  canReadUsers: boolean;
  /** Angemeldeter Benutzer; Vorschlag für den Abschlussbenutzer, sichtbar und änderbar. */
  currentAccountId: string | null;
  canWriteContracts: boolean;
  contractForm: FormInstance<ContractCreateFormValues>;
  contractTemplateBlocks: ContractTemplateBlockDto[];
  setContractTemplateBlocks: React.Dispatch<React.SetStateAction<ContractTemplateBlockDto[]>>;
  showHistoricalContracts: boolean;
  text: (key: string) => string;
  message: {
    success(content: React.ReactNode): void;
  };
  modal: {
    confirm(config: ModalFuncProps): unknown;
  };
  loadAccounts: () => Promise<unknown>;
  loadContractTemplates: () => Promise<unknown>;
  openContractPackageEdit: (record: ContractRow, single: PersonServiceRow | null) => Promise<unknown> | unknown;
  setClosingAccountId: React.Dispatch<React.SetStateAction<string>>;
  setContractCancelItem: React.Dispatch<React.SetStateAction<ContractRow | null>>;
  setContractCancelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setContractOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tryOpenCombinedOrServiceEdit: (record: PersonServiceRow) => Promise<unknown> | unknown;
  onContractsChanged: () => void;
  onContributionsChanged: () => void;
  onOpenItemsChanged: () => void;
};

type ContractBundlePerson = {
  homeStudioId?: string | null;
};

function compareContractsNewestFirst(a: ContractRow, b: ContractRow): number {
  return (
    compareDateOnlyDesc(a.startDate, b.startDate)
    || compareDateOnlyDesc(a.endDate, b.endDate)
    || compareTextDesc(a.createdAt, b.createdAt)
    || compareTextDesc(a.id, b.id)
  );
}

function sortContractsNewestFirst(rows: ContractRow[]): ContractRow[] {
  return [...rows].sort(compareContractsNewestFirst);
}

export function usePersonContractListController({
  api,
  personId,
  canCancelContracts,
  canReadUsers,
  currentAccountId,
  canWriteContracts,
  contractForm,
  contractTemplateBlocks,
  setContractTemplateBlocks,
  showHistoricalContracts,
  text,
  message,
  modal,
  loadAccounts,
  loadContractTemplates,
  openContractPackageEdit,
  setClosingAccountId,
  setContractCancelItem,
  setContractCancelOpen,
  setContractOpen,
  tryOpenCombinedOrServiceEdit,
  onContractsChanged,
  onContributionsChanged,
  onOpenItemsChanged
}: PersonContractListControllerArgs) {
  const queryClient = useQueryClient();
  const [contracts, setContracts] = React.useState<ContractRow[]>([]);

  const fetchContractPackages = React.useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.person.contractPackages(personId),
        queryFn: () => api.get<ApiContractPackageDto[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`),
        staleTime: 0
      }),
    [api, personId, queryClient]
  );

  const fetchContractBlocks = React.useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.person.contractBlocks(personId),
        queryFn: () => api.get<ApiPersonServiceDto[]>(`/api/v1/contracts?personId=${encodeURIComponent(personId)}`),
        staleTime: 0
      }),
    [api, personId, queryClient]
  );

  const fetchContractTemplateBlocks = React.useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.lookups.contractTemplateBlocks,
        queryFn: async () => {
          const list = (await api.get<ContractTemplateBlockDto[]>("/api/v1/contract-templates")) || [];
          return list.filter((x) => x.isActive !== false);
        },
        staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
      }),
    [api, queryClient]
  );

  const openContractBundle = React.useCallback(async (person: ContractBundlePerson) => {
    await loadContractTemplates();
    const contractsList = await fetchContractPackages();
    setContracts(sortContractsNewestFirst((contractsList || []).map(mapApiPackageToContractRow)));

    if (canReadUsers) {
      await loadAccounts().catch(() => {});
    }

    contractForm.setFieldsValue({
      studioId: person.homeStudioId ?? null,
      contractTemplateVariantId: "",
      startDate: dayjs(),
      trainingStartDate: dayjs(),
      closingDate: dayjs(),
      restPeriodTemplateId: null
    });
    setClosingAccountId(canReadUsers && currentAccountId ? currentAccountId : "");
    setContractOpen(true);
  }, [
    api,
    canReadUsers,
    currentAccountId,
    contractForm,
    fetchContractPackages,
    loadAccounts,
    loadContractTemplates,
    personId,
    setClosingAccountId,
    setContractOpen
  ]);

  const loadContractRows = React.useCallback(async (): Promise<{ data: ContractRow[]; success: boolean; total: number }> => {
    const [contractPackagesList, contractsList, templatesList] = await Promise.all([
      fetchContractPackages(),
      fetchContractBlocks(),
      fetchContractTemplateBlocks()
    ]);

    const currentBlocks = (templatesList || []).filter((x) => x.isActive !== false);
    if (contractTemplateBlocks.length === 0 && currentBlocks.length > 0) {
      setContractTemplateBlocks(currentBlocks);
    }

    const contractRows = sortContractsNewestFirst(buildPersonContractRows({
      contractPackages: contractPackagesList || [],
      services: contractsList || [],
      currentBlocks,
      fallbackBlocks: contractTemplateBlocks,
      showHistoricalContracts
    }));

    return { data: contractRows, success: true, total: contractRows.length };
  }, [contractTemplateBlocks, fetchContractBlocks, fetchContractPackages, fetchContractTemplateBlocks, setContractTemplateBlocks, showHistoricalContracts]);

  const loadContractPackagesForOverview = React.useCallback(async (): Promise<ContractRow[]> => {
    const contractPackages = await fetchContractPackages();
    return sortContractsNewestFirst((contractPackages || []).map(mapApiPackageToContractRow));
  }, [fetchContractPackages]);

  const isContractPackageRow = React.useCallback((record: ContractRow) => record.sourceContractPackageId == null, []);

  const canCancelContractPackage = React.useCallback(
    (record: ContractRow) => {
      if (!isContractPackageRow(record)) return false;
      const isCancelled = record.cancellationRequestedDate != null || record.cancellationEffectiveDate != null;
      return (
        (record.status === CONTRACT_STATUS_ACTIVE || record.status === CONTRACT_STATUS_PAUSED) &&
        !isCancelled &&
        // Was die Liste als „beendet“ zeigt, ist nicht mehr kündbar.
        !isEndedByDate(record)
      );
    },
    [isContractPackageRow]
  );

  const canRevokeContractPackageCancellation = React.useCallback(
    (record: ContractRow) => {
      if (!isContractPackageRow(record)) return false;
      const isCancelled = record.cancellationRequestedDate != null || record.cancellationEffectiveDate != null;
      return (
        (isCancelled || record.status === CONTRACT_STATUS_CANCELLED) &&
        record.status !== CONTRACT_STATUS_ENDED
      );
    },
    [isContractPackageRow]
  );

  const contractsActionSlots = React.useMemo(
    () => [
      {
        show: () => canWriteContracts,
        render: (record: ContractRow) => (
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              if (!isContractPackageRow(record)) {
                void tryOpenCombinedOrServiceEdit(record as unknown as PersonServiceRow);
                return;
              }
              const children = record.children || [];
              const single = children.length === 1 ? children[0] : record.singleBlockChild ?? null;
              return openContractPackageEdit(record, single);
            }}
          >
            {text("Edit")}
          </Button>
        )
      },
      {
        show: (record: ContractRow) => canCancelContracts && canCancelContractPackage(record),
        render: (record: ContractRow) => (
          <Button
            type="link"
            size="small"
            danger
            icon={<StopOutlined />}
            onClick={() => {
              setContractCancelItem(record);
              return setContractCancelOpen(true);
            }}
          >
            {text("Terminate")}
          </Button>
        )
      },
      {
        show: (record: ContractRow) => canCancelContracts && canRevokeContractPackageCancellation(record),
        render: (record: ContractRow) => (
          <Button
            type="link"
            size="small"
            icon={<UndoOutlined />}
            onClick={async () => {
              const ok = await new Promise<boolean>((resolve) => {
                modal.confirm({
                  title: text("Revoke cancellation"),
                  content: text("Do you really want to revoke the cancellation?"),
                  okText: text("Revoke"),
                  cancelText: text("Cancel"),
                  onOk: () => resolve(true),
                  onCancel: () => resolve(false)
                });
              });
              if (!ok) return;
              try {
                await api.post(`/api/v1/contract-packages/${encodeURIComponent(record.id)}/revoke-cancellation`, {});
                message.success(text("Cancellation revoked."));
                onContractsChanged();
                onContributionsChanged();
                onOpenItemsChanged();
              } catch {
                // Central API toast shows error.
              }
            }}
          >
            {text("Revoke cancellation")}
          </Button>
        )
      }
    ],
    [
      api,
      canCancelContractPackage,
      canCancelContracts,
      canRevokeContractPackageCancellation,
      canWriteContracts,
      isContractPackageRow,
      message,
      modal,
      onContractsChanged,
      onContributionsChanged,
      onOpenItemsChanged,
      openContractPackageEdit,
      setContractCancelItem,
      setContractCancelOpen,
      text,
      tryOpenCombinedOrServiceEdit
    ]
  );

  return {
    canCancelContractPackage,
    contracts,
    contractsActionSlots,
    isContractPackageRow,
    loadContractPackagesForOverview,
    loadContractRows,
    openContractBundle
  };
}
